import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import numWords from "num-words";
import { taxSlabs } from "../../constants";
import { InvoiceItemColumns } from "../../pages/invoices/constants";
import { AccountService, CRMService, InvoiceService, ProjectService, PurchaseService } from "../../services";
import { ActionButton, CardLayout, CrudTable, InputField, SelectField } from "../v3";
import { markTableReset } from "../../utils/tableStateStorage";

const DEFAULT_INVOICE_DETAILS = {
  projectId: undefined,
  date: moment().format("YYYY-MM-DD"),
  invoiceNumber: undefined,
  description: undefined,
  isTaxIncluded: false,
  buyerType: "B2B",
  type: "CUSTOM",
  vehicleNumber: "",
  bankDetails: {
    bankAccountNumber: "",
    ifscCode: "",
  },
};

const DEFAULT_ITEM = {
  name: "",
  quantity: "",
  unitPrice: "",
  hsnCode: "",
  unitOfMeasure: null,
  taxPercentage: "",
};

const formatCurrency = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const formatNumberToWords = (number) => {
  if (!number && number !== 0) return "";
  return numWords(Math.round(number));
};

const BUYER_TYPE_OPTIONS = [
  { label: "B2B", value: "B2B" },
  { label: "B2C", value: "B2C" },
];
/**
 * @param {Array<Object>} items
 * @returns {string[]}
 */
const getItemValidationMessages = (items) =>
  items.flatMap((item, index) => {
    /** @type {string[]} */
    const itemErrors = [];

    if (!String(item.name ?? "").trim()) {
      itemErrors.push("Item name is required");
    }

    if (item.quantity === "" || item.quantity == null) {
      itemErrors.push("Quantity is required");
    } else if (!Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) {
      itemErrors.push("Quantity must be greater than 0");
    }

    if (item.unitPrice === "" || item.unitPrice == null) {
      itemErrors.push("Unit price is required");
    } else if (!Number.isFinite(Number(item.unitPrice)) || Number(item.unitPrice) <= 0) {
      itemErrors.push("Unit price must be greater than 0");
    }

    return itemErrors.length ? [`Item ${index + 1}: ${itemErrors.join(", ")}`] : [];
  });
export const AddEditCustomInvoiceForm = ({ invoiceId }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState(DEFAULT_INVOICE_DETAILS);
  const [currentItem, setCurrentItem] = useState(DEFAULT_ITEM);
  const [unitSelectOptions, setUnitSelectOptions] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  const navigate = useNavigate();

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      const { data: invoice } = await InvoiceService.getInvoiceById(invoiceId);
      setInvoiceDetails({
        ...invoice,
        bankDetails: {
          bankAccountNumber: invoice?.bankDetails?.bankAccountNumber ?? "",
          ifscCode: invoice?.bankDetails?.ifscCode ?? "",
        },
        buyerType: invoice?.buyerType || "B2B",
        date: moment(invoice?.date).format("YYYY-MM-DD"),
      });
      setSelectedClient(invoice?.client);
      setSelectedProject(invoice?.project);
      setInvoiceItems(invoice?.items.map((item, index) => ({ ...item, id: item.id ?? index })));
      const savedAccountNumber = invoice?.bankDetails?.bankAccountNumber;
      const savedIfscCode = invoice?.bankDetails?.ifscCode;

      setSelectedBank(null);

      if (savedAccountNumber && savedIfscCode) {
        try {
          const { data } = await AccountService.getAccounts({
            type: "BANK",
          });

          const accounts = data?.rows ?? [];

          const matchingBank = accounts.find(
            (account) =>
              String(account.meta?.accountNumber ?? "").trim() === String(savedAccountNumber).trim() &&
              String(account.meta?.ifscCode ?? "")
                .trim()
                .toUpperCase() === String(savedIfscCode).trim().toUpperCase(),
          );

          setSelectedBank(matchingBank ?? null);
        } catch (error) {
          console.error("Failed to load bank accounts", error);
        }
      }
    } catch (error) {
      console.error(`🚀 ~ AddEditCustomInvoice.component.jsx ~ fetchInvoiceData ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceData();
    }
  }, [invoiceId]);

  useEffect(() => {
    setErrors((prev) =>
      prev.invoiceItems
        ? {
            ...prev,
            invoiceItems: getItemValidationMessages(invoiceItems).join("\n"),
          }
        : prev,
    );
  }, [invoiceItems]);

  const fetchUnits = async () => {
    try {
      const { data } = await PurchaseService.getUnitOfMeasurements();
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setUnitSelectOptions(rows.map((item) => ({ label: item.name, value: item.name })));
    } catch (error) {
      console.error("Failed to fetch units", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    setInvoiceDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleBankChange = (bank) => {
    setSelectedBank(bank);

    setInvoiceDetails((prev) => ({
      ...prev,
      bankDetails: {
        bankAccountNumber: bank?.meta?.accountNumber ?? "",
        ifscCode: bank?.meta?.ifscCode ?? "",
      },
    }));
    setErrors((prev) => ({
      ...prev,
      bankAccountNumber: "",
      ifscCode: "",
    }));
  };

  const handleBankDetailsChange = (event) => {
    const { name, value } = event.target;

    setInvoiceDetails((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setInvoiceDetails((prev) => ({ ...prev, [name]: checked }));
  };

  const updateItemState = (event) => {
    const { name, value } = event.target;
    setCurrentItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected);
    setErrors((prev) => ({ ...prev, clientId: "" }));
  };

  const handleProjectChange = (selected) => {
    setSelectedProject(selected);
    setInvoiceDetails((prev) => ({ ...prev, projectId: selected?.id }));
  };

  const handleAddItem = () => {
    const itemErrors = {};
    if (!String(currentItem.name ?? "").trim()) {
      itemErrors.name = "Item name is required";
    }

    if (!currentItem.quantity || Number(currentItem.quantity) <= 0) {
      itemErrors.quantity = "Quantity must be greater than 0";
    }

    if (!currentItem.unitPrice || Number(currentItem.unitPrice) <= 0) {
      itemErrors.unitPrice = "Unit price must be greater than 0";
    }

    if (!currentItem.unitOfMeasure) {
      itemErrors.unitOfMeasure = "Unit is required";
    }

    if (Object.keys(itemErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...itemErrors }));
      return;
    }

    setInvoiceItems((prev) => [
      ...prev,
      {
        ...currentItem,
        id: Date.now(),
        quantity: Number(currentItem.quantity),
        unitPrice: currentItem.unitPrice ? Number(currentItem.unitPrice) : undefined,
        taxPercentage: currentItem.taxPercentage ? Number(currentItem.taxPercentage) : undefined,
      },
    ]);

    // Reset item form
    setCurrentItem(DEFAULT_ITEM);
    setErrors((prev) => {
      const { name, quantity, unitPrice, unitOfMeasure, ...rest } = prev;
      return rest;
    });
  };

  const handleClearForm = () => {
    setInvoiceDetails(DEFAULT_INVOICE_DETAILS);
    setErrors({});
    setSelectedClient(null);
    setSelectedProject(null);
    setInvoiceItems([]);
    setSelectedBank(null);
    setCurrentItem(DEFAULT_ITEM);
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const calculateTaxDetails = () => {
    if (!invoiceDetails.isTaxIncluded) {
      return {
        cgstAmount: 0,
        sgstAmount: 0,
      };
    }
    const totalTax = invoiceItems.reduce((acc, item) => {
      const quantity = Number(item.quantity ?? 0);
      const unitPrice = Number(item.unitPrice ?? 0);
      const taxPercentage = Number(item.taxPercentage ?? 0);

      return acc + quantity * unitPrice * (taxPercentage / 100);
    }, 0);

    return {
      cgstAmount: Number((totalTax / 2).toFixed(2)),
      sgstAmount: Number((totalTax / 2).toFixed(2)),
    };
  };

  const handleSaveInvoice = async () => {
    if (loading) return;
    if (!invoiceDetails.date?.trim()) {
      setErrors((prev) => ({
        ...prev,
        date: "Date is required",
      }));
      return;
    }
    if (invoiceItems.length === 0) {
      setErrors({
        invoiceItems: "At least one item is required",
      });
      return;
    }

    const itemMessages = getItemValidationMessages(invoiceItems);

    if (itemMessages.length > 0) {
      setErrors({
        invoiceItems: itemMessages.join("\n"),
      });
      return;
    }

    if (!selectedClient?.id) {
      setErrors({ clientId: "Client is required" });
      return;
    }
    const accountNumber = invoiceDetails.bankDetails?.bankAccountNumber?.trim();
    const ifscCode = invoiceDetails.bankDetails?.ifscCode?.trim();

    if (ifscCode && !accountNumber) {
      setErrors({
        bankAccountNumber: "Account Number is required when IFSC Code is provided",
      });
      return;
    }

    if (accountNumber && !ifscCode) {
      setErrors({
        ifscCode: "IFSC Code is required when Account Number is provided",
      });
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      const taxDetails = calculateTaxDetails();
      if (invoiceId) {
        const payload = {
          projectId: invoiceDetails.projectId,
          date: moment(`${invoiceDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
          invoiceNumber: invoiceDetails.invoiceNumber,
          vehicleNumber: invoiceDetails.vehicleNumber,
          description: invoiceDetails.description,
          isTaxIncluded: invoiceDetails.isTaxIncluded,
          buyerType: invoiceDetails.buyerType,
          type: invoiceDetails.type,
          bankDetails: {
            bankAccountNumber: invoiceDetails.bankDetails?.bankAccountNumber ?? "",
            ifscCode: invoiceDetails.bankDetails?.ifscCode ?? "",
          },
          taxDetails,
          items: invoiceItems.map(({ id, sl, isNew, ...item }) => item),
        };
        await InvoiceService.updateInvoice(invoiceId, payload);
      } else {
        const payload = {
          ...invoiceDetails,
          date: moment(`${invoiceDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
          bankDetails: {
            bankAccountNumber: invoiceDetails.bankDetails?.bankAccountNumber ?? "",
            ifscCode: invoiceDetails.bankDetails?.ifscCode ?? "",
          },
          taxDetails,
          items: invoiceItems.map(({ id, sl, isNew, ...item }) => item),
        };
        await InvoiceService.createInvoice(selectedClient?.id, payload);
      }
      toast.success(`Invoice ${invoiceId ? "updated" : "created"} successfully`);
      handleClearForm();
      // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
      if (!invoiceId) markTableReset();
      navigate(-1);
    } catch (error) {
      console.error(`🚀 ~ AddEditCustomInvoice.component.jsx ~ handleSaveInvoice ~ error:`, error);
      toast.error(`Failed to ${invoiceId ? "update" : "create"} invoice`);
    } finally {
      setLoading(false);
    }
  };

  const invoiceItemRows = invoiceItems.map((item, index) => ({
    ...item,
    sl: index + 1,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    taxPercentage: item.taxPercentage,
  }));

  const derivedValues = useMemo(() => {
    const totalAmount = Number(
      invoiceItems.reduce((acc, item) => {
        const quantity = Number(item.quantity ?? 0);
        const unitPrice = Number(item.unitPrice ?? 0);

        return acc + quantity * unitPrice;
      }, 0),
    );

    const taxDetails = calculateTaxDetails();
    const totalTax = taxDetails.cgstAmount + taxDetails.sgstAmount;
    const totalAmountWithTax = totalAmount + totalTax;

    return {
      totalAmount,
      totalAmountWithTax,
      totalTax,
      cgstAmount: taxDetails.cgstAmount,
      sgstAmount: taxDetails.sgstAmount,
    };
  }, [invoiceItems, invoiceDetails.isTaxIncluded]);
  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <SelectField
          label="Client"
          value={selectedClient}
          getData={CRMService.getClientList}
          getDataStaticFilters={{ active: true, sortBy: "name", sortOrder: "asc" }}
          required
          error={!!errors.clientId}
          helperText={errors.clientId}
          onChange={handleClientChange}
          style={{ width: "100%" }}
        />

        <SelectField
          label="Project"
          value={selectedProject}
          disabled={!selectedClient}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ clientId: selectedClient?.id, status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />

        <InputField
          type="date"
          label="Date"
          name="date"
          value={invoiceDetails.date ?? ""}
          onChange={updateDetailsState}
          required
          error={!!errors.date}
          helperText={errors.date}
          style={{ width: "100%" }}
        />

        <InputField
          type="text"
          label="Invoice Number"
          name="invoiceNumber"
          value={invoiceDetails.invoiceNumber ?? ""}
          onChange={updateDetailsState}
          error={!!errors.invoiceNumber}
          helperText={errors.invoiceNumber}
          style={{ width: "100%" }}
        />

        <InputField
          type="text"
          label="Vehicle Number"
          name="vehicleNumber"
          value={invoiceDetails.vehicleNumber ?? ""}
          onChange={updateDetailsState}
          error={!!errors.vehicleNumber}
          helperText={errors.vehicleNumber}
          style={{ width: "100%" }}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2.5,
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          backgroundColor: "#F9FAFB",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Bank Details
        </Typography>

        <Box sx={{ mb: 2 }}>
          <SelectField
            label="Select Bank Account"
            value={selectedBank}
            getData={AccountService.getAccounts}
            getDataStaticFilters={{ type: "BANK" }}
            optionFilter={(accounts) => accounts.filter((account) => account.type === "BANK")}
            optionValueFields={["name"]}
            onChange={handleBankChange}
            style={{ width: "100%" }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 2,
          }}
        >
          <InputField
            label="Bank Account Number"
            name="bankAccountNumber"
            type="text"
            value={invoiceDetails.bankDetails?.bankAccountNumber ?? ""}
            onChange={handleBankDetailsChange}
            error={!!errors.bankAccountNumber}
            helperText={errors.bankAccountNumber}
            fullWidth
          />

          <InputField
            label="IFSC Code"
            name="ifscCode"
            type="text"
            value={invoiceDetails.bankDetails?.ifscCode ?? ""}
            onChange={handleBankDetailsChange}
            error={!!errors.ifscCode}
            helperText={errors.ifscCode}
            fullWidth
          />
        </Box>
      </Box>

      <Box display="flex" gap={3} mt={2} alignItems="center">
        <FormControlLabel
          control={<Checkbox name="isTaxIncluded" checked={invoiceDetails.isTaxIncluded} onChange={handleCheckboxChange} />}
          label="Tax Included"
          sx={{ whiteSpace: "nowrap" }}
        />

        <InputField
          select
          label="Buyer Type"
          name="buyerType"
          value={invoiceDetails.buyerType}
          selectOptions={BUYER_TYPE_OPTIONS}
          onChange={updateDetailsState}
          style={{ width: "200px" }}
        />
      </Box>
      {/* ── Add item row ── */}
      <Box display="flex" gap={2} mt={2} alignItems="flex-start" justifyContent="center">
        <InputField
          type="text"
          required
          label="Item Name"
          name="name"
          value={currentItem.name}
          onChange={updateItemState}
          error={!!errors.name}
          helperText={errors.name}
          style={{ width: "100%" }}
        />

        <InputField
          type="number"
          required
          label="Quantity"
          name="quantity"
          value={currentItem.quantity}
          onChange={updateItemState}
          error={!!errors.quantity}
          helperText={errors.quantity}
          style={{ width: "100%" }}
        />

        <InputField
          type="number"
          required
          label="Unit Price"
          name="unitPrice"
          value={currentItem.unitPrice}
          onChange={updateItemState}
          error={!!errors.unitPrice}
          helperText={errors.unitPrice}
          style={{ width: "100%" }}
        />

        <InputField
          select
          required
          label="Unit"
          name="unitOfMeasure"
          value={currentItem.unitOfMeasure || ""}
          selectOptions={unitSelectOptions}
          onChange={updateItemState}
          error={!!errors.unitOfMeasure}
          helperText={errors.unitOfMeasure}
          style={{ width: "100%" }}
        />

        <InputField
          type="text"
          label="HSN Code"
          name="hsnCode"
          value={currentItem.hsnCode}
          onChange={updateItemState}
          style={{ width: "100%" }}
        />

        <InputField
          type="number"
          label="Tax %"
          select={true}
          selectOptions={taxSlabs}
          disabled={!invoiceDetails.isTaxIncluded}
          name="taxPercentage"
          value={currentItem.taxPercentage}
          onChange={updateItemState}
          style={{ width: "100%" }}
        />

        <Box display="flex" alignItems="center">
          <ActionButton buttonText="Add" color="primary" onButtonClick={handleAddItem} />
        </Box>
      </Box>

      {/* ── Items table ── */}
      <Typography variant="caption" sx={{ color: "error.main", fontWeight: 500, whiteSpace: "pre-line", display: "block" }}>
        {errors.invoiceItems}
      </Typography>
      <CrudTable
        loading={loading}
        rows={invoiceItemRows}
        setRows={setInvoiceItems}
        columns={InvoiceItemColumns({ isTaxIncluded: invoiceDetails.isTaxIncluded })}
        hidePagination
      />

      {/* ── Invoice Summary ── */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          backgroundColor: "#F9FAFB",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Calculated Summary
        </Typography>
        <Typography variant="body2">Total Amount: ₹ {formatCurrency(derivedValues.totalAmount)}</Typography>
        <Typography variant="body2">Total Tax: ₹ {formatCurrency(derivedValues.totalTax)}</Typography>
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
          Grand Total: ₹ {formatCurrency(derivedValues.totalAmountWithTax)}
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
          Amount in Words:{" "}
          {derivedValues?.totalAmountWithTax ? `Rupees ${formatNumberToWords(derivedValues?.totalAmountWithTax)} only` : ""}
        </Typography>
      </Box>

      {/* ── Footer actions ── */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
        <ActionButton buttonText="save" color="primary" onButtonClick={handleSaveInvoice} loading={loading} />
      </Box>
    </CardLayout>
  );
};
